import { useState } from "react";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { Tape } from "@/components/Notebook";

/**
 * ShareCaseCard — a lightweight, on-brand share block for programme pages.
 * Encourages L&D managers, sponsors and peers to forward a case study.
 *
 * Props:
 *  - programmeTitle: string   (e.g. "Executive Presence for Leaders")
 *  - caseLabel?:     string   (small eyebrow — defaults to translation)
 *  - testId?:        string
 */
export const ShareCaseCard = ({ programmeTitle, caseLabel, testId = "share-case-card" }) => {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = programmeTitle
    ? `${programmeTitle} — InterAct Globally`
    : "InterAct Globally";

  const linkedInHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(t("share.email_subject"))}&body=${encodeURIComponent(`${t("share.email_body")}\n\n${shareTitle}\n${pageUrl}`)}`;
  const whatsAppHref = `https://wa.me/?text=${encodeURIComponent(`${t("share.whatsapp_body")} ${shareTitle} — ${pageUrl}`)}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* no-op */
    }
  };

  return (
    <section
      className="relative z-10 py-24 md:py-28 bg-paper-deep/50 border-y border-ink/10"
      data-testid={testId}
    >
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="relative bg-paper border border-ink/12 shadow-[6px_12px_36px_rgba(27,27,24,0.08)] p-8 md:p-12">
            <Tape className="absolute -top-4 left-10" rotate={-4} width={120} />
            <Tape className="absolute -top-4 right-10" rotate={4} width={120} />

            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              <div className="lg:col-span-6">
                <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-4">
                  {caseLabel || t("share.eyebrow")}
                </p>
                <h3 className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight text-ink">
                  {t("share.headline")}
                </h3>
                <p className="mt-5 font-body text-ink-soft leading-relaxed max-w-md">
                  {t("share.body")}
                </p>
                {programmeTitle && (
                  <p className="mt-4 font-hand text-terracotta text-lg">
                    {programmeTitle}
                  </p>
                )}
              </div>

              <div className="lg:col-span-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a
                    href={linkedInHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${testId}-linkedin`}
                    className="group flex items-center justify-between gap-4 px-5 py-4 border border-ink/15 bg-paper hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300"
                  >
                    <span className="font-body text-sm uppercase tracking-[0.14em]">
                      {t("share.linkedin")}
                    </span>
                    <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" fill="currentColor" aria-hidden="true">
                      <path d="M20.45 3H3.55A.55.55 0 0 0 3 3.55v16.9c0 .3.25.55.55.55h16.9a.55.55 0 0 0 .55-.55V3.55A.55.55 0 0 0 20.45 3zM8.4 18.34H5.66V9.72H8.4v8.62zM7.03 8.53a1.59 1.59 0 1 1 0-3.19 1.59 1.59 0 0 1 0 3.19zM18.34 18.34h-2.74v-4.18c0-1 0-2.29-1.39-2.29s-1.61 1.09-1.61 2.22v4.25h-2.74V9.72h2.63v1.18h.04c.37-.7 1.27-1.44 2.61-1.44 2.79 0 3.31 1.84 3.31 4.22v4.66z" />
                    </svg>
                  </a>

                  <a
                    href={emailHref}
                    data-testid={`${testId}-email`}
                    className="group flex items-center justify-between gap-4 px-5 py-4 border border-ink/15 bg-paper hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300"
                  >
                    <span className="font-body text-sm uppercase tracking-[0.14em]">
                      {t("share.email")}
                    </span>
                    <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <rect x="3" y="5" width="18" height="14" rx="1" />
                      <path d="M3 7l9 6 9-6" />
                    </svg>
                  </a>

                  <a
                    href={whatsAppHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${testId}-whatsapp`}
                    className="group flex items-center justify-between gap-4 px-5 py-4 border border-ink/15 bg-paper hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300"
                  >
                    <span className="font-body text-sm uppercase tracking-[0.14em]">
                      {t("share.whatsapp")}
                    </span>
                    <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" fill="currentColor" aria-hidden="true">
                      <path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .17 5.32.17 11.87c0 2.09.55 4.13 1.6 5.94L0 24l6.35-1.67a11.9 11.9 0 0 0 5.7 1.45h.01c6.55 0 11.88-5.33 11.88-11.88 0-3.17-1.24-6.15-3.42-8.42zM12.06 21.5h-.01a9.6 9.6 0 0 1-4.89-1.34l-.35-.21-3.77.99 1-3.67-.23-.38a9.58 9.58 0 0 1-1.47-5.02c0-5.3 4.32-9.62 9.63-9.62 2.57 0 4.99 1 6.8 2.82a9.55 9.55 0 0 1 2.82 6.8c0 5.31-4.32 9.63-9.63 9.63zm5.28-7.2c-.29-.14-1.71-.85-1.97-.94-.27-.1-.46-.14-.65.14-.19.29-.75.94-.92 1.14-.17.19-.34.21-.63.07-.29-.14-1.22-.45-2.32-1.43-.86-.77-1.44-1.71-1.61-2-.17-.29-.02-.44.13-.59.13-.13.29-.34.43-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.65-1.57-.9-2.15-.24-.57-.48-.49-.65-.5l-.55-.01c-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.4s1.03 2.78 1.17 2.97c.14.19 2.02 3.08 4.89 4.32.68.29 1.21.47 1.63.6.68.22 1.31.19 1.8.11.55-.08 1.71-.7 1.95-1.37.24-.68.24-1.26.17-1.37-.07-.11-.26-.19-.55-.33z" />
                    </svg>
                  </a>

                  <button
                    onClick={onCopy}
                    type="button"
                    data-testid={`${testId}-copy`}
                    className="group flex items-center justify-between gap-4 px-5 py-4 border border-ink/15 bg-paper hover:bg-ink hover:text-paper hover:border-ink transition-colors duration-300"
                  >
                    <span className="font-body text-sm uppercase tracking-[0.14em]">
                      {copied ? t("share.copied") : t("share.copy")}
                    </span>
                    {copied ? (
                      <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0 text-mustard" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                        <path d="M4 12l5 5L20 6" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <rect x="9" y="9" width="12" height="12" rx="2" />
                        <path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ShareCaseCard;
